<template>
	<section
		:class="[sectionClasses, 'current-stats-section position-relative', { 'section-overlay--dark': darkOverlay }]"
	>
		<div class="container current-stats-container bg-img-dotted px-200 py-200">
			<div class="module pl-100 pr-100">
				<ul
					class="display-flex-md justify-content-space-around gutters-150-md text-align-center pt-500-md pb-400-md pt-200 pb-100"
				>
					<li v-for="(stat, id) in currentStats" :key="id" :class="[columnClass, 'pt-100-smo pb-100-smo']">
						<BasePlaceHolder :line-count="3" :placehold="isLoading">
							<span class="heading-1 display-block mb-100">
								{{ stat?.value }}
							</span>
							{{ stat?.label }}
						</BasePlaceHolder>
					</li>
				</ul>
				<BasePlaceHolder :line-count="1" :placehold="isLoading" class="pb-100">
					<p class="mt-0 text-align-center muted eta"
						>*Total real estate value of projects invested in since inception of Rise Companies Corp
						sponsored real estate investment programs, as of {{ lastUpdated }}.</p
					>
				</BasePlaceHolder>
			</div>
		</div>
	</section>
</template>

<script setup lang="ts">
import { computed, Ref, ref } from 'vue';
import { SanityGlobalSocialStats, SanitySocialStat } from 'types/sanity/marketing-components';
import { CustomUnixSpacetimeFormat } from 'types/spacetime-formats';
import { getSocialStats } from '@api/sanity/graphql/global-social-stats';
import spacetime from 'spacetime';

interface Props {
	sectionClasses?: string;
	darkOverlay?: boolean;
}

withDefaults(defineProps<Props>(), {
	sectionClasses: '',
	darkOverlay: false
});

const currentStats: Ref<Array<SanitySocialStat | null>> = ref([]);

const lastUpdated: Ref<string | null> = ref(null);

const isLoading = ref(true);

const columnClass = computed((): string => {
	const statLength = Math.min(currentStats.value.length, 4);
	return 'col-' + 12 / statLength + '-md';
});

async function setup(): Promise<void> {
	await setupStatistics();
}
setup();

async function setupStatistics(): Promise<void> {
	const sanityStats: SanityGlobalSocialStats | null = await getSocialStats();
	const expectedStats = ['investors', 'assetValue', 'dividends'];

	if (sanityStats?.statistics) {
		expectedStats.forEach((slug) => {
			const selectedStats = sanityStats.statistics.find((s) => s.slug.current === slug);

			if (selectedStats) {
				currentStats.value.push(selectedStats);
			}
		});
	}

	if (sanityStats?.lastUpdated) {
		lastUpdated.value = spacetime(sanityStats.lastUpdated).unixFmt(
			CustomUnixSpacetimeFormat.UNIX_SINGLE_DIGIT_FULL_YEAR
		);
	}

	isLoading.value = !(currentStats.value && lastUpdated.value);
}
</script>

<style lang="scss" scoped>
@use '../../styles/constants/colors' as *;
@use '../../styles/constants/layout';

.current-stats-section {
	z-index: 1;

	&:after {
		content: '';
		width: 100%;
		height: 75%;
		background: token('greige');
		position: absolute;
		bottom: 0;
		z-index: -1;

		@media screen and (max-width: #{layout.$md-start - 1}) {
			height: 25%;
		}
	}

	&.section-overlay--dark:after {
		background: token('content-primary');
	}

	.current-stats-container {
		@media screen and (max-width: #{layout.$lg-start - 1}) {
			background-position: center center;
			background-repeat: repeat;
		}

		.module {
			box-shadow: none;
		}
	}
}
</style>
